import { useEffect, useRef, useState } from "react";
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Eye,
  EyeOff,
  ListOrdered,
  ListTodo,
} from "lucide-react";
import Tooltip from "../ui/Tooltip";
import AnimatePresence from "../ui/AnimatePresence";
import { useDialog } from "../../contexts/DialogContext";
import { useView } from "../../contexts/ViewContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import FilterPopover from "./FilterPopover";

type OpenPopover = "sort" | null;

export default function FilterRowViewControls() {
  const {
    sortByPriority,
    cycleSortByPriority,
    sortByCreatedDate,
    cycleSortByCreatedDate,
    showCompleted,
    toggleShowCompleted,
    showArchived,
    toggleShowArchived,
    allSubtasksExpanded,
    toggleAllSubtasksExpanded,
  } = useView();
  const dialog = useDialog();
  const isMobile = useIsMobile();
  const shortcutHintsActive = dialog.isOpen("shortcut-hints");
  const [openPopover, setOpenPopover] = useState<OpenPopover>(null);
  const sortBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (shortcutHintsActive) {
      setOpenPopover(null);
    }
  }, [shortcutHintsActive]);

  const toggleSortPopover = () => {
    setOpenPopover((prev) => (prev === "sort" ? null : "sort"));
  };

  const sortActive = sortByPriority !== "off" || sortByCreatedDate !== "off";

  return (
    <>
      <div className="w-px h-5 bg-warm-border flex-shrink-0 mx-3" />

      <div className="flex items-center gap-2 flex-shrink-0">
        <Tooltip
          content={
            allSubtasksExpanded
              ? "Collapse all subtasks (+)"
              : "Expand all subtasks (+)"
          }
          placement="bottom"
        >
          <button
            onClick={toggleAllSubtasksExpanded}
            aria-pressed={allSubtasksExpanded}
            aria-label={
              allSubtasksExpanded
                ? "Collapse all subtasks"
                : "Expand all subtasks"
            }
            data-shortcut="+"
            data-shortcut-pad="0"
            data-shortcut-badge-position="above-right"
            className={`p-2 rounded-full transition-all duration-150 ${
              allSubtasksExpanded
                ? "bg-warm-blue text-white shadow-soft-sm"
                : "bg-warm-bg-elevated text-warm-text-secondary hover:bg-warm-border-subtle hover:scale-105"
            }`}
          >
            <ListTodo size={16} />
          </button>
        </Tooltip>

        <Tooltip
          content={
            showCompleted
              ? "Hide completed tasks (H)"
              : "Show completed tasks (H)"
          }
          placement="bottom"
        >
          <button
            onClick={toggleShowCompleted}
            aria-pressed={showCompleted}
            aria-label={
              showCompleted ? "Hide completed tasks" : "Show completed tasks"
            }
            data-shortcut="H"
            data-shortcut-pad="0"
            data-shortcut-badge-position="above-right"
            className={`p-2 rounded-full transition-all duration-150 ${
              showCompleted
                ? "bg-warm-blue text-white shadow-soft-sm"
                : "bg-warm-bg-elevated text-warm-text-secondary hover:bg-warm-border-subtle hover:scale-105"
            }`}
          >
            {showCompleted ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </Tooltip>

        <Tooltip
          content={
            showArchived ? "Back to active tasks" : "View archived tasks"
          }
          placement="bottom"
        >
          <button
            onClick={toggleShowArchived}
            aria-pressed={showArchived}
            aria-label={
              showArchived ? "Back to active tasks" : "View archived tasks"
            }
            className={`p-2 rounded-full transition-all duration-150 ${
              showArchived
                ? "bg-warm-blue text-white shadow-soft-sm"
                : "bg-warm-bg-elevated text-warm-text-secondary hover:bg-warm-border-subtle hover:scale-105"
            }`}
          >
            <Archive size={16} />
          </button>
        </Tooltip>
      </div>

      <div className="w-px h-5 bg-warm-border flex-shrink-0 mx-3" />

      <button
        ref={sortBtnRef}
        onClick={toggleSortPopover}
        aria-expanded={openPopover === "sort"}
        aria-haspopup="dialog"
        aria-label={sortActive ? "Sort (active)" : "Sort"}
        className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-full transition-all duration-150 whitespace-nowrap flex-shrink-0 border ${
          sortActive
            ? "border-warm-blue bg-warm-blue/10 text-warm-blue"
            : "border-warm-border bg-transparent text-warm-text-secondary hover:bg-warm-bg-elevated hover:scale-105"
        }`}
      >
        <ArrowUpDown size={14} />
        <span>Sort</span>
        <ChevronDown
          size={12}
          className={`transition-transform duration-200 ease-out ${openPopover === "sort" ? "rotate-180" : ""}`}
        />
      </button>

      {!isMobile && (
        <Tooltip content="Reorder lists (R)" placement="bottom">
          <button
            type="button"
            onClick={() => dialog.open("reorder-lists")}
            aria-label="Reorder lists"
            data-shortcut="R"
            data-shortcut-pad="0"
            data-shortcut-badge-position="above-right"
            className="flex items-center gap-1.5 px-3.5 py-1.5 ml-2 text-sm font-semibold rounded-full transition-all duration-150 whitespace-nowrap flex-shrink-0 border border-warm-border bg-transparent text-warm-text-secondary hover:bg-warm-bg-elevated hover:scale-105"
          >
            <ListOrdered size={14} />
            <span>Reorder</span>
          </button>
        </Tooltip>
      )}

      <AnimatePresence popover>
        {openPopover === "sort" && (
          <FilterPopover
            anchorRef={sortBtnRef}
            onClose={() => setOpenPopover(null)}
            ariaLabel="Sort options"
          >
            <div className="flex flex-col gap-1">
              <button
                onClick={cycleSortByPriority}
                aria-pressed={sortByPriority !== "off"}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                  sortByPriority !== "off"
                    ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                    : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                }`}
              >
                {sortByPriority === "asc" ? (
                  <ArrowUp size={14} />
                ) : sortByPriority === "desc" ? (
                  <ArrowDown size={14} />
                ) : (
                  <ArrowUpDown size={14} />
                )}
                <span>Priority</span>
              </button>
              <button
                onClick={cycleSortByCreatedDate}
                aria-pressed={sortByCreatedDate !== "off"}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out text-left ${
                  sortByCreatedDate !== "off"
                    ? "bg-warm-blue/10 text-warm-blue dark:bg-warm-blue/15 dark:text-warm-blue"
                    : "text-warm-text-secondary hover:bg-warm-bg-elevated"
                }`}
              >
                {sortByCreatedDate === "asc" ? (
                  <ArrowUp size={14} />
                ) : sortByCreatedDate === "desc" ? (
                  <ArrowDown size={14} />
                ) : (
                  <ArrowUpDown size={14} />
                )}
                <span>Created date</span>
              </button>
            </div>
          </FilterPopover>
        )}
      </AnimatePresence>
    </>
  );
}
